import React, { useEffect, useState, useImperativeHandle, forwardRef } from "react";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  CardElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";

let _stripePromise = null;
function getStripe() {
  if (!_stripePromise) {
    const key = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
    if (!key) {
      console.error("Missing REACT_APP_STRIPE_PUBLISHABLE_KEY");
      return null;
    }
    _stripePromise = loadStripe(key);
  }
  return _stripePromise;
}

const CARD_OPTIONS = {
  style: {
    base: {
      color: "#f3ece0",
      fontFamily: "Inter, system-ui, sans-serif",
      fontSize: "15px",
      "::placeholder": { color: "rgba(35,38,28,0.35)" },
      iconColor: "#4F5D1E",
    },
    invalid: {
      color: "#f87171",
      iconColor: "#f87171",
    },
  },
};

const CardFormInner = forwardRef(function CardFormInner({ onError }, ref) {
  const stripe = useStripe();
  const elements = useElements();
  const [cardError, setCardError] = useState(null);

  useImperativeHandle(ref, () => ({
    /** Returns a Stripe PaymentMethod id (pm_…) or throws */
    async createPaymentMethod({ name, email }) {
      if (!stripe || !elements) {
        throw new Error("Stripe.js is still loading. Try again in a moment.");
      }
      const card = elements.getElement(CardElement);
      if (!card) throw new Error("Card details required.");
      const { error, paymentMethod } = await stripe.createPaymentMethod({
        type: "card",
        card,
        billing_details: { name: name || undefined, email: email || undefined },
      });
      if (error) {
        setCardError(error.message);
        onError?.(error.message);
        throw new Error(error.message);
      }
      setCardError(null);
      return paymentMethod.id;
    },
    /** Optional: handle 3DS challenge with PaymentIntent client_secret returned by server */
    async confirmCardAction(clientSecret) {
      if (!stripe) throw new Error("Stripe.js not ready.");
      const { error, paymentIntent } = await stripe.confirmCardPayment(clientSecret);
      if (error) throw new Error(error.message);
      return paymentIntent;
    },
    clear() {
      const card = elements?.getElement(CardElement);
      card?.clear();
      setCardError(null);
    },
  }), [stripe, elements, onError]);

  return (
    <div>
      <div
        className="if-input !p-3 !py-3.5"
        data-testid="card-element-wrapper"
        style={{ minHeight: 48 }}
      >
        <CardElement
          options={CARD_OPTIONS}
          onChange={(e) => setCardError(e.error?.message || null)}
        />
      </div>
      {cardError && (
        <p className="text-xs text-red-400 mt-1.5" data-testid="card-element-error">
          {cardError}
        </p>
      )}
      <p className="text-[10px] text-white/40 mt-2 leading-relaxed" style={{ fontFamily: "Inter, sans-serif" }}>
        Card details go directly to Stripe — Infantry Financial never sees or stores the number.
      </p>
    </div>
  );
});

const StripeCardForm = forwardRef(function StripeCardForm(props, ref) {
  const [stripePromise] = useState(() => getStripe());
  if (!stripePromise) {
    return (
      <div className="text-xs text-red-400 p-3 rounded bg-red-500/10 border border-red-500/30">
        Stripe publishable key missing. Set REACT_APP_STRIPE_PUBLISHABLE_KEY in /app/frontend/.env.
      </div>
    );
  }
  return (
    <Elements stripe={stripePromise}>
      <CardFormInner ref={ref} {...props} />
    </Elements>
  );
});

export default StripeCardForm;
